import { AuditOutlined } from '@ant-design/icons';
import { CustomBreadcrumb } from 'components/CustomBreadcrumb';
import { EditLayout } from 'components/layout/layout/EditLayout';
import { Flex, Tabs, Tag } from 'antd';
import { HttpError, useCan, useOne } from '@refinedev/core';
import { PageHeader } from 'components/professor_area/PageHeader';
import { TabAttempts } from './components/TabAttempts';
import { TabGeneral } from './components/TabGeneral';
import { handleWizardError } from 'utils/error-handler';
import { replaceTimeZone } from 'services/date';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCustomSearchParams } from 'hooks/useSearchParams';
import { useForm } from '@refinedev/antd';
import { useParams } from 'react-router-dom';
import { useProfessorAreaAssignmentsStore } from './assignments.store';
import type {
  IProfessorAreaEvaluationEdit,
  IProfessorAreaEvaluationShow
} from 'interfaces/professor_area/evaluations';
import type { IProfessorClassroom } from 'interfaces/professor_area/classrooms';

export const ProfessorAreaAssignmentEdit = () => {
  const paramas = useParams();
  const classroomId = paramas.classroomId;
  const evaluationId = paramas.id;

  const setSelectedEvaluation = useProfessorAreaAssignmentsStore(
    s => s.setSelectedEvaluation
  );

  const { currentSearchParams: currentTab, setSearchParams } =
    useCustomSearchParams({
      searchParams: 'tab'
    });

  const [activeTabKey, setActiveTabKey] = useState<string>(
    currentTab || 'general'
  );
  const { data: attemptsPermission } = useCan({
    resource: 'professor_area/classroom_assignments/attempts',
    action: 'list'
  });
  const isAttemptsPermissionResolved = attemptsPermission !== undefined;
  const canListAttempts = Boolean(attemptsPermission?.can);

  const { data, isLoading: classroomDetailsLoading } =
    useOne<IProfessorClassroom>({
      resource: 'professor_area/classrooms',
      id: classroomId
    });

  const { formProps, queryResult, formLoading, form, onFinish } = useForm<
    IProfessorAreaEvaluationShow,
    HttpError,
    IProfessorAreaEvaluationEdit
  >({
    resource: 'professor_area/classroom_assignments',
    meta: { classroomId },
    action: 'edit',
    id: evaluationId,
    errorNotification: () => {
      return {
        message: 'Erro ao atualizar atividade. Tente novamente!',
        description: 'Erro',
        type: 'error'
      };
    },
    successNotification: () => {
      return {
        message: 'Atividade atualizada com sucesso',
        description: 'Sucesso',
        type: 'success'
      };
    }
  });

  const isLoading = useMemo(() => {
    if (queryResult) {
      const { isLoading } = queryResult;
      return isLoading && classroomDetailsLoading;
    }

    return false;
  }, [queryResult, classroomDetailsLoading]);

  const record = useMemo(() => queryResult?.data?.data, [queryResult]);
  const classroomDetails = useMemo(() => data?.data, [data]);

  useEffect(() => {
    if (record && classroomDetails) {
      setSelectedEvaluation({
        evaluation: {
          id: record?.id,
          name: record.name,
          assignment_id: record?.evaluation_id,
          maxAttempts: record?.max_attempts,
          passing_sore: record?.passing_score
        },
        classroom: {
          code: classroomDetails?.code,
          discipline: classroomDetails?.discipline_name,
          id: classroomDetails?.id
        }
      });
    }
  }, [record, classroomDetails, setSelectedEvaluation]);

  const onTabChange = useCallback(
    (tab: string) => {
      setActiveTabKey(tab);
      setSearchParams(tab);
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (
      isAttemptsPermissionResolved &&
      !canListAttempts &&
      activeTabKey === 'attempts'
    ) {
      setActiveTabKey('general');
      setSearchParams('general');
    }
  }, [
    activeTabKey,
    canListAttempts,
    isAttemptsPermissionResolved,
    setSearchParams
  ]);

  const onSubmit = useCallback(async () => {
    try {
      await form.validateFields();
    } catch (error: unknown) {
      handleWizardError(form, error);
      return;
    }

    const { starts_at, ends_at, ...rest } = form.getFieldsValue();

    // Match the create flow: pin the wall-clock time to the São Paulo offset.
    // Guarded because replaceTimeZone(undefined) would resolve to "now" — the
    // date fields are absent when the access period is relative.
    const formData = {
      ...rest,
      ...(starts_at ? { starts_at: replaceTimeZone(starts_at) } : {}),
      ...(ends_at ? { ends_at: replaceTimeZone(ends_at) } : {})
    };

    try {
      await onFinish(formData);
    } catch {
      // Reported by refine's errorNotification on the useForm above.
    }
  }, [form, onFinish]);

  return (
    <EditLayout
      isLoading={isLoading}
      footerButtonProps={{ hidden: true }}
      breadcrumb={
        <CustomBreadcrumb
          label="Área do professor"
          backButton
          items={[
            {
              label: 'Turmas',
              path: '/professor_area/classrooms'
            },
            {
              label: 'Visualizar turma',
              path: `/professor_area/classrooms/show/${classroomId}?tab=assignments`
            },
            {
              label: 'Editar'
            },
            {
              label: 'Atividades'
            }
          ]}
        />
      }
    >
      <PageHeader
        title={record?.name || ''}
        icon={AuditOutlined}
        meta={
          <Flex gap={4}>
            <Tag>Atividade</Tag>
            <Tag>{classroomDetails?.code}</Tag>
            <Tag>{classroomDetails?.discipline_name}</Tag>
          </Flex>
        }
      />

      {record && (
        <Tabs
          activeKey={activeTabKey}
          onChange={onTabChange}
          className="custom-tabs"
        >
          <Tabs.TabPane tab="Visão geral" key="general">
            <TabGeneral
              formProps={formProps}
              onSubmit={onSubmit}
              data={record}
              isLoading={formLoading}
            />
          </Tabs.TabPane>
          {canListAttempts && (
            <Tabs.TabPane tab="Atividades realizadas" key="attempts">
              <TabAttempts />
            </Tabs.TabPane>
          )}
        </Tabs>
      )}
    </EditLayout>
  );
};
